import { isAxiosError } from 'axios'

import type { ApiClient } from 'clients/yogi-api'
import type { NotificationDto } from 'features/notifications/service/types'

/**
 * 403 = the caller isn't allowed the notifications surface at all
 * (internal-only rollout). Distinct from transient errors: the container
 * hides the bell and the list query stops polling. Lives at the service
 * seam because sniffing transport errors is the service layer's job.
 */
export const isForbiddenError = (err: unknown): boolean =>
  isAxiosError(err) && err.response?.status === 403

/**
 * Service for the v2 client/notifications surface. Reads the signed-in
 * user's notifications (paged), toggles their read state, and
 * soft-deletes rows.
 *
 * Backend contract (PROD-3836):
 *   GET    /client/notifications?only_unread=…&limit=…&offset=…  -> 200 NotificationDto[]
 *   PUT    /client/notifications/{id}/read                       -> 204
 *   PUT    /client/notifications/{id}/unread                     -> 204
 *   DELETE /client/notifications/{id}                            -> 204 (soft delete)
 */
export class NotificationsService {
  constructor(private readonly client: ApiClient) {}

  async fetchMyNotifications(
    only_unread: boolean,
    limit: number,
    offset: number,
  ): Promise<NotificationDto[]> {
    const { data } = await this.client.get<NotificationDto[]>(
      '/client/notifications',
      { params: { only_unread, limit, offset } },
    )
    return data
  }

  async markAsRead(notificationId: number): Promise<void> {
    await this.client.put(`/client/notifications/${notificationId}/read`)
  }

  async markAsUnread(notificationId: number): Promise<void> {
    await this.client.put(`/client/notifications/${notificationId}/unread`)
  }

  async deleteNotification(notificationId: number): Promise<void> {
    await this.client.delete(`/client/notifications/${notificationId}`)
  }
}
